import { Check } from 'lucide-react';
import type { Variants } from 'motion/react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn.js';
import { EASE_OUT_QUINT } from '../motion.js';

/*
 * As peças de um assistente em etapas (design.md §8, Assistente em etapas),
 * comuns ao novo relatório e à nova obra: a seção de uma etapa, o bloco e o
 * dado da revisão, o deslize entre etapas e onde o foco cai em cada uma. Um só
 * lugar, para os dois assistentes não divergirem em espaçamento nem em gesto.
 */

/**
 * A troca de etapa desliza no sentido em que se anda: avançando, a nova entra
 * pela direita; voltando, pela esquerda. A anterior só se apaga, depressa.
 */
export const DESLIZE: Variants = {
  entra: (direcao: number) => ({ opacity: 0, x: 18 * direcao }),
  parado: { opacity: 1, x: 0, transition: { duration: 0.28, ease: EASE_OUT_QUINT } },
  sai: (direcao: number) => ({
    opacity: 0,
    x: -10 * direcao,
    transition: { duration: 0.12, ease: 'easeIn' },
  }),
};

/**
 * O campo que leva o foco numa etapa, em ordem de preferência: a opção
 * escolhida, a primeira que se pode escolher ou o primeiro campo. Nunca um
 * botão — no rascunho recuperado, o primeiro botão da etapa é "Descartar
 * rascunho".
 */
export const CAMPO_DA_ETAPA = [
  '[role="radio"][aria-checked="true"]',
  '[role="radio"]:not(:disabled), input:not([type="file"]):not([type="radio"]), textarea',
] as const;

/**
 * Põe o foco no primeiro campo da etapa que acabou de entrar. Sem campo (a
 * revisão), no título dela — que o leitor de tela anuncia —, mas só se o foco
 * ficou sem lugar: o botão que levou até ali pode ter saído do rodapé.
 */
export function focarNaEtapa(etapa: HTMLElement): void {
  // A etapa pode apontar o campo por onde se começa (`data-foco-da-etapa`).
  const campo = ['[data-foco-da-etapa]', ...CAMPO_DA_ETAPA].reduce<HTMLElement | null>(
    (achado, seletor) => achado ?? etapa.querySelector<HTMLElement>(seletor),
    null,
  );
  if (campo) {
    campo.focus({ preventScroll: true });
    return;
  }
  const raiz = etapa.closest('dialog, main') ?? document.body;
  if (!raiz.contains(document.activeElement) || document.activeElement === document.body) {
    etapa.querySelector<HTMLElement>('[data-titulo-da-etapa]')?.focus({ preventScroll: true });
  }
}

/** Uma seção de etapa: o título em versalete com o filete que corre até a borda. */
export function Secao({
  titulo,
  children,
  className,
}: {
  titulo: string;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <section className={cn('flex flex-col gap-4', className)}>
      <h4 className="flex items-center gap-3 text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">
        {titulo}
        <span aria-hidden className="h-px flex-1 bg-line" />
      </h4>
      {children}
    </section>
  );
}

/** Um bloco da revisão: o título numa faixa, e o "Alterar" que volta à etapa. */
export function Bloco({
  titulo,
  onAlterar,
  children,
}: {
  titulo: string;
  onAlterar?: (() => void) | undefined;
  children: ReactNode;
}) {
  return (
    <section className="border border-line">
      <header className="flex items-center justify-between gap-3 border-b border-line bg-surface-2 px-4 py-2">
        <h4 className="text-[11px] font-medium tracking-[0.06em] text-ink-3 uppercase">{titulo}</h4>
        {onAlterar ? (
          <button
            type="button"
            onClick={onAlterar}
            className="text-[12px] font-medium text-primary-ink transition-colors hover:text-primary"
          >
            Alterar
          </button>
        ) : null}
      </header>
      <div className="px-4 py-3.5">{children}</div>
    </section>
  );
}

/**
 * Uma opção em cartão, num grupo de escolha única (`role="radiogroup"` em quem
 * o contém): o gesto do tipo de registro do novo relatório. A escolhida ganha o
 * filete laranja, o fundo `primary-soft` e o visto no quadrado à direita. A
 * compacta é a pílula de uma linha, para opções curtas (situação, intervalo).
 */
export function OpcaoEmCartao({
  ativa,
  onEscolher,
  onConfirmar,
  icone,
  titulo,
  descricao,
  compacta = false,
}: {
  ativa: boolean;
  onEscolher: () => void;
  /** Dois cliques: escolhe e já avança. */
  onConfirmar?: (() => void) | undefined;
  icone?: ReactNode;
  titulo: string;
  descricao?: ReactNode;
  compacta?: boolean | undefined;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={ativa}
      onClick={onEscolher}
      onDoubleClick={onConfirmar}
      className={cn(
        'flex text-left transition-[border-color,background-color,box-shadow] duration-150',
        compacta ? 'items-center gap-2.5 border px-3.5 py-2.5' : 'items-start gap-3.5 border p-4',
        ativa
          ? 'border-primary bg-primary-soft/45 shadow-[inset_0_0_0_1px_var(--color-primary)]'
          : 'border-line bg-surface hover:border-line-2 hover:bg-surface-2',
      )}
    >
      {icone ? (
        <span
          className={cn(
            'grid shrink-0 place-items-center transition-colors duration-150',
            compacta ? 'size-5' : 'size-10',
            !compacta && (ativa ? 'bg-primary text-on-primary' : 'bg-sunken text-ink-2'),
          )}
        >
          {icone}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block font-semibold tracking-tight text-ink',
            compacta ? 'text-[13px]' : 'text-[14px]',
          )}
        >
          {titulo}
        </span>
        {descricao ? (
          <span className="mt-0.5 block text-[13px] text-ink-3">{descricao}</span>
        ) : null}
      </span>
      <span
        aria-hidden
        className={cn(
          'grid shrink-0 place-items-center border transition-colors duration-150',
          compacta ? 'size-4' : 'size-5',
          ativa
            ? 'border-primary bg-primary text-on-primary'
            : 'border-line-2 bg-surface text-transparent',
        )}
      >
        <Check size={compacta ? 10 : 12} strokeWidth={3} />
      </span>
    </button>
  );
}

/** Um par rótulo e valor da revisão. */
export function Dado({
  rotulo,
  children,
  largo = false,
}: {
  rotulo: string;
  children: ReactNode;
  largo?: boolean;
}) {
  return (
    <div className={cn(largo && 'sm:col-span-2')}>
      <dt className="text-[12px] text-ink-3">{rotulo}</dt>
      <dd className="mt-0.5 text-[14px] break-words text-ink">{children}</dd>
    </div>
  );
}
